import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface LegalSite {
  id: string;
  name: string;
  url: string;
  category: string | null;
  description: string | null;
  courtOrState: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLegalSiteInput {
  name: string;
  url: string;
  category?: string | null;
  description?: string | null;
  courtOrState?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export type UpdateLegalSiteInput = CreateLegalSiteInput;

type UseLegalSitesOptions = {
  autoLoad?: boolean;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toBoolean = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['0', 'false', 'inactive', 'inativo', 'archived', 'arquivado', 'nao', 'não'].includes(normalized)) {
      return false;
    }
    if (['1', 'true', 'active', 'ativo'].includes(normalized)) {
      return true;
    }
  }

  return Boolean(value);
};

const normalizeLegalSite = (value: any): LegalSite => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? '').trim(),
  url: String(value?.url ?? '').trim(),
  category: toNullableText(value?.category),
  description: toNullableText(value?.description),
  courtOrState: toNullableText(value?.courtOrState ?? value?.court_or_state),
  notes: toNullableText(value?.notes),
  isActive: toBoolean(value?.isActive ?? value?.is_active),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

const sortLegalSites = (a: LegalSite, b: LegalSite) =>
  (a.category ?? '').localeCompare(b.category ?? '', 'pt-BR') || a.name.localeCompare(b.name, 'pt-BR');

const sortAndSetLegalSites = (
  setLegalSites: Dispatch<SetStateAction<LegalSite[]>>,
  next: LegalSite[]
) => {
  setLegalSites([...next].sort(sortLegalSites));
};

export function useLegalSites(options: UseLegalSitesOptions = {}) {
  const { autoLoad = true } = options;
  const [legalSites, setLegalSites] = useState<LegalSite[]>([]);
  const [isLoadingLegalSites, setIsLoadingLegalSites] = useState(true);
  const [legalSitesError, setLegalSitesError] = useState<string | null>(null);

  const loadLegalSites = useCallback(async () => {
    setIsLoadingLegalSites(true);
    setLegalSitesError(null);

    try {
      const response = await fetchWithRetry('/api/legal-sites');
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.legalSites)) {
        throw new Error(data.error || `GET /api/legal-sites falhou com status ${response.status}`);
      }

      sortAndSetLegalSites(setLegalSites, data.legalSites.map(normalizeLegalSite));
    } catch (error) {
      console.error('Erro ao carregar sites jurídicos.', error);
      setLegalSites([]);
      setLegalSitesError('Não foi possível carregar os sites jurídicos.');
    } finally {
      setIsLoadingLegalSites(false);
    }
  }, []);

  const createLegalSite = useCallback(async (input: CreateLegalSiteInput) => {
    const response = await fetchWithRetry('/api/legal-sites', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.legalSite) {
      throw new Error(data.error || `POST /api/legal-sites falhou com status ${response.status}`);
    }

    const created = normalizeLegalSite(data.legalSite);
    setLegalSites((prev) => [created, ...prev.filter((site) => site.id !== created.id)].sort(sortLegalSites));
    return created;
  }, []);

  const updateLegalSite = useCallback(async (id: string, input: UpdateLegalSiteInput) => {
    const response = await fetch(`/api/legal-sites/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.legalSite) {
      throw new Error(data.error || `PUT /api/legal-sites/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeLegalSite(data.legalSite);
    setLegalSites((prev) => [updated, ...prev.filter((site) => site.id !== updated.id)].sort(sortLegalSites));
    return updated;
  }, []);

  const archiveLegalSite = useCallback(async (id: string) => {
    const response = await fetch(`/api/legal-sites/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/legal-sites/${id} falhou com status ${response.status}`);
    }

    setLegalSites((prev) => prev.filter((site) => site.id !== id));
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      setIsLoadingLegalSites(false);
      return;
    }

    void loadLegalSites();
  }, [autoLoad, loadLegalSites]);

  return {
    legalSites,
    isLoadingLegalSites,
    legalSitesError,
    loadLegalSites,
    createLegalSite,
    updateLegalSite,
    archiveLegalSite,
  };
}
